import { Card, CardBody, CardHeader, Button, Spinner } from '@heroui/react'
import { useState } from 'react'

import { AnalyticsService, type DatabaseAnalyticsResult } from '../services'

interface DatabaseAnalyticsProps {
  className?: string
}

export function DatabaseAnalytics({ className }: DatabaseAnalyticsProps) {
  const [analyticsData, setAnalyticsData] =
    useState<DatabaseAnalyticsResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleGetDatabaseAnalytics = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await AnalyticsService.getDatabaseAnalytics()
      setAnalyticsData(result)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to get analytics'
      setError(errorMessage)
      console.error('Database analytics error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={className}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <div className="text-2xl">📊</div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Database Analytics
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Overall database statistics
                </p>
              </div>
            </div>
            <Button
              color="primary"
              variant="flat"
              size="sm"
              onPress={handleGetDatabaseAnalytics}
              isLoading={isLoading}
              isDisabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Get Analytics'}
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          {!analyticsData && !isLoading && !error && (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">📈</div>
              <p className="text-slate-600 dark:text-slate-400">
                Click &quot;Get Analytics&quot; to view database statistics
              </p>
            </div>
          )}

          {isLoading && (
            <div className="text-center py-8">
              <Spinner size="lg" />
              <p className="mt-4 text-slate-600 dark:text-slate-400">
                Analyzing database...
              </p>
            </div>
          )}

          {analyticsData && (
            <div className="space-y-6">
              {/* Overview Statistics */}
              <div>
                <h4 className="text-md font-semibold text-slate-900 dark:text-slate-100 mb-3">
                  Overview
                </h4>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {analyticsData.totalDocuments.toLocaleString()}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      Total Documents
                    </div>
                  </div>
                  <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {(analyticsData.documentsWithText ?? 0).toLocaleString()}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      With Text
                    </div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {(
                        analyticsData.documentsWithGifUrl ?? 0
                      ).toLocaleString()}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      With GIF URL
                    </div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                      {(
                        analyticsData.documentsWithSource ?? 0
                      ).toLocaleString()}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      With Source
                    </div>
                  </div>
                </div>
              </div>

              {/* Last Updated */}
              <div className="text-xs text-slate-500 dark:text-slate-500 text-center">
                Last updated:{' '}
                {new Date(analyticsData.lastUpdated).toLocaleString()}
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}
